import React from "react";
import Loader from "react-loader-spinner";
import Swal from "sweetalert2/dist/sweetalert2.js";
import debounce from "lodash.debounce";
import { connect } from "react-redux";
import { Table } from "react-bootstrap";
import { castToISTDefaultDateFormat, castToISTTimeFormat } from "../../../settings/index";
import requireAuth from "../../../hoc/requireAuth.js";
import Footer from "../../../common/Footer.js";
import AdminSideNav from "../../../common/AdminSideNav.js";
import AnalystSideNav from "../../../common/AnalystSideNav.js";
import TopNav from "../../../common/TopNav.js";
import authorizeAnalystOrAdmin from "../../../hoc/authorizeAnalystOrAdmin.js";
import "../../../assets/css/data-upload.css";
import "sweetalert/dist/sweetalert.css";

const optionsMap = [
  { label: "Social Scan", value: "social_scan" },
  { label: "Screenshot", value: "screenshot" },
  { label: "Flipkart Search", value: "flipkart" },
  { label: "Flipkart Max Search", value: "flipkartMaxSearch" },
  { label: "Flipkart pid", value: "flipkartPid" },
];

const platformMap = [
  {
    value: "social-media",
    label: "Social Media",
    enabled: (report) =>
      ![
        "social_scan",
        "facebookScan",
        "instagramScan",
        "telegramScan",
        "youtubeScan",
      ].includes(report),
  },
  {
    value: "e-commerce",
    label: "E-commerce",
    enabled: (report) =>
      ![
        "facebookScan",
        "instagramScan",
        "screenshot",
        "telegramScan",
        "social_scan",
      ].includes(report),
  },
  {
    value: "domain",
    label: "Domain",
    enabled: (report) => !["screenshot"].includes(report),
  },
  {
    value: "website",
    label: "Website",
    enabled: (report) => !["screenshot"].includes(report),
  },
  {
    value: "app-store",
    label: "App Store",
    enabled: (report) => !["screenshot"].includes(report),
  },
];

class QuickTools extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      emailId: "",
      report: "",
      platform: "",
      urls: [],
      queues: [],
      pagination: {
        page: 1,
        nextPage: 1,
        prevPage: null,
        hasNextPage: true,
        hasPrevPage: false,
        pagingCounter: 1,
        totalDocs: null,
        totalPages: null,
      },
      perPage: 10,
      isLoading: false,
      hasMoreDomainData: true,
    };
    this.listofRecords = debounce(this.listofRecords.bind(this), 500);
    this.handleScroll = debounce(() => {
      if (!this.state.hasMoreDomainData) return;
      if (
        window.innerHeight + document.documentElement.scrollTop ===
        document.documentElement.offsetHeight
      ) {
        this.listofRecords();
      }
    }, 100);
  }

  componentDidMount() {
    window.addEventListener("scroll", this.handleScroll);
    this.listofRecords();
    this.getEmailId();
  }

  componentWillUnmount() {
    window.removeEventListener("scroll", this.handleScroll);
  }

  async getEmailId() {
    try {
      const res = await window.axios.get("/users/getEmailId", {
        params: {
          page: 1,
          perPage: 400,
        },
      });

      this.setState({
        emailId: res.data.data,
      });
    } catch (error) {
      console.error("Failed to fetch email IDs:", error);
    }
  }

  async postQueueConfirmation() {
    const username = this.props.login?.user?.username;
    const noOfDocument = [
      "social_scan",
      "flipkart",
      "flipkartMaxSearch",
      "facebookScan",
      "instagramScan",
      "youtubeScan",
      "telegramScan",
    ].includes(this.state.report)
      ? 1
      : this.state.urls.length;

    const reportLabel =
      optionsMap.find((opt) => opt.value === this.state.report)?.label ||
      this.state.report;

    try {
      const result = await Swal.fire({
        title: "Confirm Action",
        text: ["flipkart", "flipkartMaxSearch"].includes(this.state.report)
          ? `You are about to run ${reportLabel} with search term: ${this.state.urls[0]}`
          : `You are about to run ${reportLabel}. This query will fetch ${noOfDocument} record(s).`,
        icon: "question",
        showCancelButton: true,
        showConfirmButton: true,
        confirmButtonText: "OK",
        cancelButtonText: "Cancel",
      });

      if (result.isConfirmed) {
        const body = {
          email: this.state.emailId,
          username,
          numOfRecords: noOfDocument,
          report: this.state.report,
          urls: this.state.urls,
          platform: this.state.platform,
        };

        await window.axios.post("/queue/postQueueRecord", body);
        this.setState({ report: "", platform: "", urls: [] });
        this.listofRecords(true);
        Swal.fire("Saved!", "", "success");
      }
    } catch (error) {
      alert(error.response?.data?.message || "An error occurred");
    }
  }

  async listofRecords(reArrangeRecords = false) {
    const reqData = {
      page: reArrangeRecords ? 1 : this.state.pagination.nextPage,
      perPage: this.state.perPage,
    };

    this.setState({ isLoading: true });
    const reports = optionsMap.map((el) => el.value);

    try {
      const res = await window.axios.get("/queue/getRecords", {
        params: { ...reqData, report: reports },
      });

      const queueData = reArrangeRecords
        ? res.data.docs
        : [...this.state.queues, ...res.data.docs];

      this.setState({
        queues: queueData,
        isLoading: false,
        pagination: {
          page: res.data.page,
          nextPage: res.data.nextPage,
          prevPage: res.data.prevPage,
          hasNextPage: res.data.hasNextPage,
          hasPrevPage: res.data.hasPrevPage,
          pagingCounter: res.data.pagingCounter,
          totalDocs: res.data.totalDocs,
          totalPages: res.data.totalPages,
        },
        hasMoreDomainData: res.data.hasNextPage,
      });
    } catch (error) {
      this.setState({ isLoading: false });
      console.error("Error fetching records:", error);
    }
  }

  render() {
    return (
      <div className="row">
        {this.props?.login?.user?.role === "Admin" ? (
          <AdminSideNav />
        ) : (
          <AnalystSideNav />
        )}

        <main className="main-content col-lg-10 col-md-9 col-sm-12 p-0 offset-lg-2 offset-md-3">
          <TopNav />

          <div className="main-content-container container-fluid px-4">
            <div className="page-header row no-gutters py-4">
              <div className="col-12">
                <div className="row">
                  <div className="col-12 col-sm-4 text-sm-left mb-0">
                    <span className="text-uppercase page-subtitle">
                      Quick Tools
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="col">
              <div className="card card-small mb-4">
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-12">
                      <div className="row">
                        {/* Reports Dropdown */}
                        <div className="col-md-3">
                          <div className="form-group">
                            <span className="text-muted d-block mb-2">
                              Reports
                            </span>
                            <select
                              value={this.state.report}
                              onChange={(e) =>
                                this.setState({
                                  report: e.target.value,
                                  platform: "",
                                  urls: [],
                                })
                              }
                              className="form-control"
                            >
                              <option value="">Select report</option>
                              {optionsMap.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>

                        {/* Email Field */}
                        <div className="col-md-3">
                          <div className="form-group">
                            <span className="text-muted d-block mb-2">
                              Email
                            </span>
                            <input
                              className="form-control"
                              value={this.state.emailId}
                              disabled
                              readOnly
                            />
                          </div>
                        </div>

                        {/* Conditional Fields Based on Selected Report */}
                        {["flipkart", "flipkartMaxSearch"].includes(
                          this.state.report,
                        ) && (
                          <div className="col-md-3">
                            <div className="form-group">
                              <span className="text-muted d-block mb-2">
                                Search
                              </span>
                              <input
                                className="form-control form-control-md"
                                placeholder="Enter search term or URL"
                                value={this.state.urls[0] || ""}
                                onChange={(e) =>
                                  this.setState({ urls: [e.target.value] })
                                }
                              />
                            </div>
                          </div>
                        )}

                        {["screenshot", "flipkartPid"].includes(
                          this.state.report,
                        ) && (
                          <div className="col-md-3">
                            <div className="form-group">
                              <span className="text-muted d-block mb-2">
                                File
                              </span>
                              <input
                                type="file"
                                className="form-control form-control-md"
                                accept=".txt"
                                onChange={(e) => {
                                  const file = e.target.files[0];
                                  if (file?.type === "text/plain") {
                                    const reader = new FileReader();
                                    reader.onload = (ev) => {
                                      const lines = ev.target.result
                                        .split("\n")
                                        .map((line) => line.trim())
                                        .filter(Boolean);
                                      this.setState({ urls: lines });
                                    };
                                    reader.readAsText(file);
                                  } else {
                                    alert("Please upload a valid .txt file");
                                  }
                                }}
                              />
                            </div>
                          </div>
                        )}

                        {[
                          "social_scan",
                          "facebookScan",
                          "instagramScan",
                          "telegramScan",
                          "screenshot",
                        ].includes(this.state.report) && (
                          <div className="col-md-3">
                            <div className="form-group">
                              <span className="text-muted d-block mb-2">
                                Platform
                              </span>
                              <select
                                value={this.state.platform}
                                onChange={(e) =>
                                  this.setState({ platform: e.target.value })
                                }
                                className="form-control"
                              >
                                <option value="">Select the platform</option>
                                {platformMap.map((el) => (
                                  <option
                                    key={el.value}
                                    value={el.value}
                                    disabled={el.enabled(this.state.report)}
                                  >
                                    {el.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                        )}

                        {this.state.report === "youtubeScan" && (
                          <div className="col-md-3">
                            <div className="form-group">
                              <span className="text-muted d-block mb-2">
                                Platform
                              </span>
                              <select
                                value={this.state.platform}
                                onChange={(e) =>
                                  this.setState({ platform: e.target.value })
                                }
                                className="form-control"
                              >
                                <option value="">Select from dropdown</option>
                                <option value="social-media">
                                  Social Media
                                </option>
                              </select>
                            </div>
                          </div>
                        )}

                        {/* Start Processing Button */}
                        <div className="col-md-3 d-flex align-items-end">
                          <div className="form-group d-flex justify-content-center w-100">
                            <button
                              onClick={() => this.postQueueConfirmation()}
                              className="btn btn-ldotr w-100"
                              disabled={(() => {
                                const { report, platform, urls } = this.state;
                                const scanReports = [
                                  "social_scan",
                                  "facebookScan",
                                  "instagramScan",
                                  "telegramScan",
                                  "youtubeScan",
                                ];
                                const needsUrl =
                                  !scanReports.includes(report) &&
                                  urls.length === 0;
                                const needsPlatform =
                                  [...scanReports, "screenshot"].includes(
                                    report,
                                  ) && !platform;
                                return !report || needsUrl || needsPlatform;
                              })()}
                            >
                              Start Processing
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col">
                <div className="card card-small mb-4">
                  <div className="card-body p-0">
                    <Table hover className="table mb-0">
                      <thead className="bg-light uppercase-th">
                        <tr>
                          <th width="3%">Sr.</th>
                          <th width="10%">Date & Time added</th>
                          <th width="8%">Username</th>
                          <th width="8%">Report</th>
                          <th width="6%">Platform</th>
                          <th width="6%">No of Records</th>
                          <th width="8%">Status</th>
                          <th width="8%">Download</th>
                        </tr>
                      </thead>
                      <tbody>
                        {this.state.queues.map((queue, index) => (
                          <tr key={queue._id || index}>
                            <td>{index + 1}</td>
                            <td>
                              {queue.DateCreated &&
                                `${castToISTDefaultDateFormat(queue.DateCreated)}
                                 ${castToISTTimeFormat(queue.DateCreated)}`}
                            </td>
                            <td title={queue.username}>{queue.username}</td>
                            <td title={queue.report}>
                              {optionsMap.find(
                                (el) => el.value === queue?.report,
                              )?.label || queue.report}
                            </td>
                            <td>{queue.platform ? queue.platform : "-"}</td>
                            <td>{queue.numOfRecords}</td>
                            <td>
                              <span
                                className={`badge ${
                                  queue.status === "Processed"
                                    ? "bg-success"
                                    : queue.status === "Pending"
                                      ? "bg-warning text-dark"
                                      : queue.status === "Failed"
                                        ? "bg-danger"
                                        : "bg-secondary"
                                }`}
                              >
                                {queue.status}
                              </span>
                            </td>
                            <td>
                              {queue.downloadURL &&
                              queue.status !== "Pending" ? (
                                <a href={queue.downloadURL} download>
                                  <button className="btn btn-ldotr">
                                    Download
                                  </button>
                                </a>
                              ) : (
                                <button className="btn btn-ldotr" disabled>
                                  Download
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </div>

                {this.state.isLoading && (
                  <div className="text-center">
                    <Loader
                      type="ThreeDots"
                      color="#00BFFF"
                      height={10}
                      width={200}
                      timeout={5000}
                    />
                  </div>
                )}

                {this.state.hasMoreDomainData && (
                  <div className="text-center">
                    <button
                      className="btn btn-link text-danger"
                      onClick={() => this.listofRecords()}
                      disabled={this.state.isLoading}
                    >
                      {this.state.isLoading ? "Loading..." : "Load more"}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
          <Footer />
        </main>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  login: state.login,
});

export default connect(mapStateToProps)(
  authorizeAnalystOrAdmin(requireAuth(QuickTools)),
);
